import { EmptyState, formatDate } from "../components";
import { ClientReportSectionContent } from "../client-report-content";
import { projectSectionForClientPreview } from "../client-preview-projection";
import {
  CLIENT_PORTAL_REVIEW_ACTION_LABEL,
  CLIENT_PORTAL_REVIEW_INVALIDATION_MESSAGE,
  clientPortalPreviewCanBeReviewed,
  resolveClientReportContentStates,
  type ClientContentState,
} from "../report-content-state";
import { clientPortalPreviewReviewIsCurrent } from "../report-approval";
import { evaluateReportNativeReadiness } from "../report-readiness";
import { resolveReportAuditEngagementId } from "../section4-evidence";
import { usePortal } from "../store";
import { clientPortalPreviewPath } from "../router";
import { resolveClientPresentation } from "../client-presentation";
import type { ReportSection } from "../domain";

const stateLabels:Record<ClientContentState,string>={
  "Current approved":"Ready",
  "Unapproved replacement":"Needs approval",
  "Stale preserved wording":"Needs review",
  "Legacy approved wording":"Needs review",
  "Unavailable":"Not available",
};
const sectionStateLabel=(section:ReportSection,state:ClientContentState)=>
  resolveClientPresentation(section).resolution==="Formatting needs review"?"Formatting needs review":stateLabels[state];

export function ClientPortalPreviewPage({
  reportId,
  versionId,
  sectionId,
  navigate,
}:{
  reportId:string;
  versionId:string;
  sectionId?:string;
  navigate:(path:string)=>void;
}) {
  const {state,dispatch,persistence}=usePortal();
  const report=state.reports.find(item=>item.id===reportId);
  const version=report?.versions.find(item=>item.id===versionId);
  const organisation=report?state.organisations.find(item=>item.id===report.organisationId):undefined;
  if(!report||!version||!organisation){
    return <main className="client-portal-preview-page"><EmptyState title="Client portal preview not available">The requested local report version could not be found.</EmptyState></main>;
  }
  const currentReport=report,currentVersion=version;
  const auditEngagementId=resolveReportAuditEngagementId(state,currentReport);
  const details=state.clientDetails.find(item=>item.organisationId===currentReport.organisationId);
  const native=evaluateReportNativeReadiness(currentVersion,state,auditEngagementId,details);
  const contentStates=resolveClientReportContentStates(currentVersion.sections,native.readinessById);
  const selected=currentVersion.sections.find(item=>item.id===sectionId)??currentVersion.sections[0];
  const index=currentVersion.sections.findIndex(item=>item.id===selected.id);
  const selectedState=contentStates[selected.id];
  const selectedPresentation=resolveClientPresentation(selected);
  const projected=selectedPresentation.document&&selectedPresentation.current?selected:projectSectionForClientPreview(selected,selectedState);
  const locked=["Approved","Published","Superseded","Archived"].includes(currentVersion.status);
  const allCurrent=currentVersion.sections.every(item=>contentStates[item.id].currentForApproval);
  const canReview=clientPortalPreviewCanBeReviewed(contentStates,locked,persistence.status);
  const reviewCurrent=clientPortalPreviewReviewIsCurrent(currentVersion);
  const target=(targetId:string)=>clientPortalPreviewPath(currentReport.id,currentVersion.id,targetId);
  const editPath=`/reports/${currentReport.id}?section=${encodeURIComponent(selected.id)}&area=client`;
  const continuePath=`/reports/${currentReport.id}?section=work-programme&area=client`;
  const warning=currentVersion.sections.some(item=>contentStates[item.id].persistentPageWarningRequired);
  function markReviewed(){
    if(!canReview)return;
    dispatch({type:"review-report-version",reportId:currentReport.id,versionId:currentVersion.id});
  }
  function printable(){
    const url=`/reports/${currentReport.id}/preview/${currentVersion.id}`;
    const opened=window.open(url,"_blank","noopener,noreferrer");
    if(!opened)navigate(url);
  }
  return <div className="client-portal-preview-page">
    <header className="client-preview-header">
      <div>
        <p className="eyebrow">Client portal preview</p>
        <h1>{currentReport.title}</h1>
        <p>{organisation.name}</p>
      </div>
      <dl>
        {currentVersion.auditDate&&<div><dt>Audit date</dt><dd>{formatDate(currentVersion.auditDate)}</dd></div>}
        <div><dt>Report version</dt><dd>{currentVersion.number}</dd></div>
      </dl>
    </header>
    <div className="client-preview-only-banner" role="status"><strong>Preview only</strong><span>This page does not approve or publish the report.</span></div>
    {warning&&<div className="client-preview-page-warning" role="alert"><strong>Review needed</strong><span>One or more sections need review or approval before this report can continue.</span></div>}
    <nav className="client-preview-toolbar" aria-label="Client preview actions">
      <button onClick={()=>navigate(`/reports/${currentReport.id}`)}>Back to report editor</button>
      <button onClick={()=>navigate(editPath)}>Edit this section</button>
      <button onClick={printable}>Preview printable report</button>
    </nav>
    <label className="client-preview-mobile-selector">Report section
      <select value={selected.id} onChange={event=>navigate(target(event.target.value))}>
        {currentVersion.sections.map(item=><option value={item.id} key={item.id}>{item.order}. {item.heading} · {sectionStateLabel(item,contentStates[item.id].state)}</option>)}
      </select>
    </label>
    <div className="client-preview-layout">
      <nav className="client-preview-section-rail" aria-label="Client preview sections">
        {currentVersion.sections.map(item=><button key={item.id} className={item.id===selected.id?"selected":""} aria-current={item.id===selected.id?"page":undefined} onClick={()=>navigate(target(item.id))}><span>{item.order}</span><strong>{item.heading}</strong><small>{sectionStateLabel(item,contentStates[item.id].state)}</small></button>)}
      </nav>
      <main className="client-preview-section-document" id="client-preview-current-section" tabIndex={-1}>
        <p className="client-preview-position" aria-live="polite">Section {index+1} of {currentVersion.sections.length}</p>
        <div className={`client-preview-state-label state-${selectedState.state.toLowerCase().replaceAll(" ","-")}`}><strong>{sectionStateLabel(selected,selectedState.state)}</strong>{selectedState.state!=="Current approved"&&<span>Needs attention</span>}</div>
        <h2>{selected.heading}</h2>
        {selectedState.contentAvailable
          ? <ClientReportSectionContent section={projected} organisationName={organisation.name} locked={locked} reviewPresentation/>
          : <p>No client wording is currently available for this section.</p>}
        <nav className="client-preview-pager" aria-label="Previous and next report sections">
          {index>0?<button onClick={()=>navigate(target(currentVersion.sections[index-1].id))}>← Previous section</button>:<span/>}
          {index<currentVersion.sections.length-1?<button onClick={()=>navigate(target(currentVersion.sections[index+1].id))}>Next section →</button>:<span/>}
        </nav>
      </main>
    </div>
    <aside className="client-preview-review-panel">
      <div><h2>Client portal review</h2><p>Records that you have reviewed the current client-visible wording and layout. {CLIENT_PORTAL_REVIEW_INVALIDATION_MESSAGE}</p>{reviewCurrent&&<p className="confirmation-record">This exact client-visible version has been reviewed.</p>}{!allCurrent&&<p className="validation-hint">Review is unavailable until every section contains current approved, publishable wording.</p>}</div>
      <div className="client-preview-review-actions">
        <button className="primary-button" disabled={!canReview} onClick={markReviewed}>{CLIENT_PORTAL_REVIEW_ACTION_LABEL}</button>
        {reviewCurrent&&<button onClick={()=>navigate(continuePath)}>Return to report editor and continue</button>}
      </div>
    </aside>
  </div>;
}
